import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * SEO Head Component
 * Dynamically updates meta tags for better SEO
 */
export const SEOHead = ({
  title,
  description,
  image,
  url,
  type = 'website',
  locale = 'en_US',
  siteName = 'MagicStay Homes',
  twitterCard = 'summary_large_image',
  keywords = [],
  author = 'MagicStay Homes',
  publishedTime,
  modifiedTime,
  schema
}) => {
  const location = useLocation();
  const currentUrl = url || `https://property-match-150.preview.emergentagent.com${location.pathname}`;
  const defaultDescription = 'Find your perfect vacation rental in Orlando, Florida. MagicStay Homes connects you directly with property owners for the best deals near Disney and Universal.';
  const defaultImage = 'https://property-match-150.preview.emergentagent.com/og-image.jpg';

  useEffect(() => {
    // Update document title
    document.title = title ? `${title} | ${siteName}` : siteName;

    // Helper function to update or create meta tags
    const updateMeta = (name, content, property = false) => {
      if (!content) return;
      
      const attr = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Basic meta tags
    updateMeta('description', description || defaultDescription);
    updateMeta('keywords', keywords.length > 0 ? keywords.join(', ') : 'vacation rental, Orlando, Florida, Disney, Universal, holiday home, aluguel de temporada');
    updateMeta('author', author);
    updateMeta('robots', 'index, follow');

    // Open Graph meta tags
    updateMeta('og:title', title || siteName, true);
    updateMeta('og:description', description || defaultDescription, true);
    updateMeta('og:image', image || defaultImage, true);
    updateMeta('og:url', currentUrl, true);
    updateMeta('og:type', type, true);
    updateMeta('og:locale', locale, true);
    updateMeta('og:site_name', siteName, true);

    // Twitter Card meta tags
    updateMeta('twitter:card', twitterCard);
    updateMeta('twitter:title', title || siteName);
    updateMeta('twitter:description', description || defaultDescription);
    updateMeta('twitter:image', image || defaultImage);

    // Article meta tags (for blog posts)
    if (type === 'article') {
      if (publishedTime) updateMeta('article:published_time', publishedTime, true);
      if (modifiedTime) updateMeta('article:modified_time', modifiedTime, true);
    }

    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', currentUrl);

    // Add JSON-LD Schema
    if (schema) {
      let scriptTag = document.querySelector('script[type="application/ld+json"]');
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.setAttribute('type', 'application/ld+json');
        document.head.appendChild(scriptTag);
      }
      scriptTag.textContent = JSON.stringify(schema);
    }

  }, [title, description, image, currentUrl, type, locale, siteName, keywords, author, publishedTime, modifiedTime, schema]);

  return null; // This component doesn't render anything
};

/**
 * Generate Schema.org JSON-LD for different page types
 */
export const generateSchema = {
  // Organization schema for the site
  organization: () => ({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'MagicStay Homes',
    url: 'https://property-match-150.preview.emergentagent.com',
    logo: 'https://property-match-150.preview.emergentagent.com/logo.png',
    description: 'Vacation rental platform connecting guests with property owners in Orlando, Florida',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Orlando',
      addressRegion: 'FL',
      addressCountry: 'US'
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+1-302-724-1066',
      contactType: 'customer service',
      email: 'magicstayhomes@gmail.com'
    },
    sameAs: [
      'https://facebook.com/magicstayhomes',
      'https://instagram.com/magicstayhomes'
    ]
  }),

  // Property/VacationRental schema
  property: (property) => ({
    '@context': 'https://schema.org',
    '@type': 'VacationRental',
    name: property.title,
    description: property.description,
    image: property.images?.map(img => img.image_url) || [],
    address: {
      '@type': 'PostalAddress',
      addressLocality: property.city,
      addressRegion: property.state,
      addressCountry: 'US'
    },
    geo: property.latitude && property.longitude ? {
      '@type': 'GeoCoordinates',
      latitude: property.latitude,
      longitude: property.longitude
    } : undefined,
    numberOfRooms: property.bedrooms,
    numberOfBathroomsTotal: property.bathrooms,
    occupancy: {
      '@type': 'QuantitativeValue',
      maxValue: property.max_guests
    },
    amenityFeature: property.amenities?.map(amenity => ({
      '@type': 'LocationFeatureSpecification',
      name: amenity,
      value: true
    })) || [],
    priceRange: `$${(property.price || property.nightly_price_suggestion)}/night`,
    aggregateRating: property.host?.average_rating ? {
      '@type': 'AggregateRating',
      ratingValue: property.host.average_rating,
      reviewCount: property.host.total_reviews || 0
    } : undefined
  }),

  // Search results page schema
  searchResults: (properties) => ({
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: properties.slice(0, 10).map((property, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'VacationRental',
        name: property.title,
        url: `https://property-match-150.preview.emergentagent.com/property/${property.id}`,
        image: property.images?.[0]?.image_url,
        address: {
          '@type': 'PostalAddress',
          addressLocality: property.city,
          addressRegion: property.state
        }
      }
    }))
  }),

  // FAQ schema
  faq: (faqs) => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  }),

  // Breadcrumb schema
  breadcrumb: (items) => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  })
};

export default SEOHead;
